<div class="mx-auto flex min-h-dvh max-w-4xl flex-col px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))] text-(--p-text-color) sm:px-6 sm:pt-6">

  <!-- Header: Back · title · Refresh · Empty Trash -->
  <header class="flex items-center gap-2 sm:gap-3">
    <button
      type="button"
      class="-ml-2 inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full hover:bg-(--p-content-hover-background) focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      (click)="back()"
      aria-label="Back to notes"
      pTooltip="Back to notes"
      tooltipPosition="bottom">
      <i class="pi pi-arrow-left !text-xl" aria-hidden="true"></i>
    </button>
    <div class="min-w-0 flex-1">
      <h1 class="flex items-center gap-2 text-2xl font-semibold tracking-tight">
        <span class="material-symbols-outlined !text-[1.75rem] leading-none" aria-hidden="true">delete</span>
        Trash
      </h1>
      <p class="hidden text-sm text-(--p-text-muted-color) sm:block">
        Deleted notes are kept for {{ retentionDays }} days, then deleted permanently.
      </p>
    </div>
    <button
      type="button"
      class="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full hover:bg-(--p-content-hover-background) focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
      (click)="load()"
      [disabled]="state() === 'loading' || bulkBusy()"
      aria-label="Refresh Trash"
      pTooltip="Refresh"
      tooltipPosition="bottom">
      <i class="pi !text-lg" [ngClass]="state() === 'loading' ? 'pi-spinner pi-spin' : 'pi-refresh'" aria-hidden="true"></i>
    </button>
    @if (state() === 'ready' && items().length) {
      <p-button
        label="Empty Trash"
        icon="pi pi-trash"
        severity="danger"
        [outlined]="true"
        [loading]="bulkBusy()"
        [disabled]="bulkBusy()"
        (onClick)="confirmEmptyTrash()"
        styleClass="!min-h-11 whitespace-nowrap" />
    }
  </header>
  <p class="mt-1 text-xs text-(--p-text-muted-color) sm:hidden">
    Deleted notes are kept for {{ retentionDays }} days, then deleted permanently.
  </p>

  @switch (state()) {
    @case ('loading') {
      <div class="mt-6 space-y-2" aria-busy="true" aria-live="polite">
        <span class="sr-only">Loading Trash…</span>
        @for (row of [1, 2, 3, 4]; track row) {
          <div class="h-[4.25rem] animate-pulse rounded-lg bg-(--p-content-hover-background)"></div>
        }
      </div>
    }

    @case ('offline') {
      <div class="mt-16 flex flex-col items-center px-6 text-center">
        <span class="material-symbols-outlined !text-5xl text-(--p-text-muted-color)" aria-hidden="true">cloud_off</span>
        <h2 class="mt-3 text-lg font-semibold">Trash needs a connection</h2>
        <p class="mt-1 max-w-sm text-sm text-(--p-text-muted-color)">
          Trash is kept on the server. Notes you delete while offline are moved there as soon as you're back online.
        </p>
      </div>
    }

    @case ('error') {
      <div class="mt-16 flex flex-col items-center px-6 text-center" role="alert">
        <span class="material-symbols-outlined !text-5xl text-red-500" aria-hidden="true">error</span>
        <h2 class="mt-3 text-lg font-semibold">Trash couldn't be loaded</h2>
        <p class="mt-1 max-w-sm text-sm text-(--p-text-muted-color)">{{ errorMessage() }}</p>
        <p-button label="Try again" icon="pi pi-refresh" [outlined]="true" (onClick)="load()" styleClass="mt-4 !min-h-11" />
      </div>
    }

    @default {
      @if (!items().length) {
        <!-- Empty state -->
        <div class="mt-16 flex flex-col items-center px-6 text-center">
          <span class="material-symbols-outlined !text-6xl text-(--p-text-muted-color)" aria-hidden="true">delete</span>
          <h2 class="mt-3 text-lg font-semibold">Trash is empty</h2>
          <p class="mt-1 max-w-sm text-sm text-(--p-text-muted-color)">
            Deleted notes are kept for up to {{ retentionDays }} days before being permanently deleted.
          </p>
        </div>
      } @else {
        <!-- Search -->
        <p-iconfield class="mt-4 block">
          <p-inputicon styleClass="pi pi-search" />
          <input
            pInputText
            type="search"
            class="w-full !min-h-11"
            placeholder="Search deleted notes..."
            aria-label="Search deleted notes"
            [ngModel]="search()"
            (ngModelChange)="search.set($event)" />
        </p-iconfield>

        <!-- Selection toolbar (sticky so the bulk actions stay reachable while scrolling a long Trash) -->
        <div class="sticky top-0 z-10 -mx-4 mt-3 flex min-h-14 flex-wrap items-center gap-x-3 gap-y-2 border-b border-(--p-content-border-color) bg-(--p-content-background) px-4 py-1.5 sm:mx-0 sm:px-2">
          <label class="flex min-h-11 cursor-pointer items-center gap-3 pr-2 text-sm">
            <p-checkbox
              [binary]="true"
              [ngModel]="allVisibleSelected()"
              (ngModelChange)="toggleAllVisible($event)"
              [disabled]="!visibleItems().length || bulkBusy()"
              inputId="trash-select-all"
              ariaLabel="Select all" />
            <span>
              @if (selectedCount()) {
                {{ selectedCount() }} selected
              } @else {
                Select all
              }
            </span>
          </label>
          @if (selectedCount()) {
            <div class="ml-auto flex items-center gap-2">
              <p-button
                label="Restore"
                icon="pi pi-replay"
                [outlined]="true"
                [loading]="bulkBusy()"
                [disabled]="bulkBusy()"
                (onClick)="restoreSelected()"
                styleClass="!min-h-11" />
              <p-button
                label="Delete permanently"
                icon="pi pi-times-circle"
                severity="danger"
                [text]="true"
                [disabled]="bulkBusy()"
                (onClick)="confirmDeleteSelected()"
                styleClass="!min-h-11" />
            </div>
          }
        </div>

        <!-- Deleted notes -->
        <ul class="divide-y divide-(--p-content-border-color)" aria-label="Deleted notes">
          @for (item of visibleItems(); track item._id) {
            <li
              class="flex cursor-pointer items-center gap-2 py-2 sm:gap-3 sm:rounded-md sm:px-2 sm:hover:bg-(--p-content-hover-background)"
              [ngClass]="{ 'opacity-60': isBusy(item._id), 'bg-(--p-highlight-background)': isSelected(item._id) }"
              (click)="toggleSelected(item._id)">
              <div class="flex h-11 w-11 flex-shrink-0 items-center justify-center" (click)="$event.stopPropagation()">
                <p-checkbox
                  [binary]="true"
                  [ngModel]="isSelected(item._id)"
                  (ngModelChange)="toggleSelected(item._id, $event)"
                  [disabled]="bulkBusy()"
                  [ariaLabel]="'Select ' + titleOf(item)" />
              </div>
              <div class="min-w-0 flex-1">
                <div class="truncate font-medium">{{ item.Title | noteDisplayTitle: undefined }}</div>
                <!-- Phones: deletion date and retention on separate lines so neither is cut off. -->
                <div class="mt-0.5 text-xs text-(--p-text-muted-color) sm:truncate">
                  <span class="block truncate sm:inline" [pTooltip]="deletedAtTooltip(item)" tooltipPosition="bottom">{{ deletedLabel(item) }}</span>
                  @if (retentionLabel(item)) {
                    <span class="hidden sm:inline" aria-hidden="true"> · </span><span class="block truncate sm:inline">{{ retentionLabel(item) }}</span>
                  }
                </div>
                <div class="truncate text-xs text-(--p-text-muted-color)">
                  {{ spaceName(item) }}@if (item.NoteType) {<span aria-hidden="true"> · </span>{{ item.NoteType }}}
                </div>
              </div>
              <!-- Row actions: icon buttons (44px) on phones, labelled buttons from sm up -->
              <div class="flex flex-shrink-0 items-center gap-1" (click)="$event.stopPropagation()">
                <p-button
                  icon="pi pi-replay"
                  [label]="'Restore'"
                  [text]="true"
                  [loading]="busyIds().has(item._id)"
                  [disabled]="isBusy(item._id)"
                  (onClick)="restore(item)"
                  [ariaLabel]="'Restore ' + titleOf(item)"
                  styleClass="!min-h-11 !min-w-11 max-sm:[&_.p-button-label]:hidden" />
                <p-button
                  icon="pi pi-times-circle"
                  [label]="'Delete permanently'"
                  severity="danger"
                  [text]="true"
                  [disabled]="isBusy(item._id)"
                  (onClick)="confirmPermanentDelete(item)"
                  [ariaLabel]="'Delete ' + titleOf(item) + ' permanently'"
                  styleClass="!min-h-11 !min-w-11 max-sm:[&_.p-button-label]:hidden" />
              </div>
            </li>
          } @empty {
            <li class="py-10 text-center text-sm text-(--p-text-muted-color)">
              No deleted notes match "{{ search() }}".
            </li>
          }
        </ul>
      }
    }
  }
</div>
